import { useCallback, useState } from 'react';
import { ApiError } from 'src/api/axiosClient/types';
import { useInviteUserApi } from 'src/api/users';
import { buildFormData } from '../helpers';
import { useAdminCompanyService } from '../services';
import { AdminCompany, CreateAdminCompanyFormData } from '../types';
import { InviteUserData } from '../components/CreateAdminCompanyForm/InviteUserForm';
import { useSelector } from 'react-redux';
import { selectRoles } from 'src/modules/profile/slices';

export interface CreateAdminCompanyFormDialogController {
  isCreateCompanyLoading: boolean;
  isOpen: boolean;
  initFormData: CreateAdminCompanyFormData | null;
  error: ApiError | null;
  open: () => Promise<void>;
  close: () => void;
  submit: (
    company: AdminCompany,
    userData: InviteUserData,
    nextAction?: (company: AdminCompany) => unknown | Promise<unknown>
  ) => Promise<void>;
}

export const useCreateAdminCompanyFormDialogController = (): CreateAdminCompanyFormDialogController => {
  const { loading, error } = useAdminCompanyService();
  const inviteUser = useInviteUserApi();
  const { roles } = useSelector(selectRoles);
  const [initFormData, setInitFormData] = useState<CreateAdminCompanyFormData | null>(null);

  const open = useCallback(async () => {
    setInitFormData(buildFormData());
  }, []);

  const close = useCallback(() => {
    setInitFormData(null);
  }, []);

  const submit = useCallback(
    async (
      company: AdminCompany,
      userData: InviteUserData,
      nextAction?: (company: AdminCompany) => Promise<unknown>
    ) => {
      try {
        // Find the company_admin role
        const adminRole = roles.find(role => role.name === 'company_admin');

        if (!adminRole) {
          throw new Error('Company admin role not found');
        }

        // Invite the user to the existing company
        await inviteUser([
          {
            ...userData,
            phoneNumber: userData.phoneNumber ? "+1" + userData.phoneNumber : "",
            roleId: adminRole.id,
          }],
          company.id
        );

        close();
        await nextAction?.(company);
      } catch (e) {
        console.log(e);
      }
    },
    [inviteUser, close, roles]
  );

  return {
    isCreateCompanyLoading: loading.create,
    isOpen: !!initFormData,
    initFormData,
    error,
    open,
    close,
    submit,
  };
};
